import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Row, Col, Alert } from 'react-bootstrap';
import '../styles/Events.css';

const Events = () => {
  // State for events data and UI controls
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [specialEvents, setSpecialEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialEventsLoading, setSpecialEventsLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cancellation detection state
  const [cancellationInfo, setCancellationInfo] = useState(null);
  const [checkingCancellation, setCheckingCancellation] = useState(true);
  
  // Calendar states
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendar, setCalendar] = useState([]);

  // Google Calendar API details
  const CALENDAR_ID = process.env.REACT_APP_GOOGLE_CALENDAR_ID;
  const API_KEY = process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY; // Public API key for Google Calendar
  const TIMEZONE = 'US/Central'; // Central Time with automatic daylight savings handling

  // Helper function to format date in Central Time
  const formatDate = (date, options) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      ...options,
      timeZone: TIMEZONE
    });
  };

  // Helper function to format time from Date object
  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: TIMEZONE
    }).format(date);
  };

  // Parse RRULE string to extract recurrence information
  const parseRecurrence = useCallback((recurrenceArray) => {
    if (!recurrenceArray || recurrenceArray.length === 0) {
      return null;
    }
    
    const rrule = recurrenceArray[0]; // Get the first RRULE
    const recurrenceInfo = {
      frequency: null,
      interval: 1,
      daysOfWeek: [],
      endDate: null,
      count: null,
      until: null,
      originalRrule: rrule
    };
    
    // Parse FREQ (frequency)
    const freqMatch = rrule.match(/FREQ=([^;]+)/);
    if (freqMatch) {
      recurrenceInfo.frequency = freqMatch[1].toLowerCase();
    }
    
    // Parse INTERVAL
    const intervalMatch = rrule.match(/INTERVAL=(\d+)/);
    if (intervalMatch) {
      recurrenceInfo.interval = parseInt(intervalMatch[1]);
    }
    
    // Parse BYDAY (days of week)
    const bydayMatch = rrule.match(/BYDAY=([^;]+)/);
    if (bydayMatch) {
      recurrenceInfo.daysOfWeek = bydayMatch[1].split(',');
    }
    
    // Parse UNTIL (end date)
    const untilMatch = rrule.match(/UNTIL=([^;]+)/);
    if (untilMatch) {
      recurrenceInfo.until = untilMatch[1];
      recurrenceInfo.endDate = new Date(untilMatch[1].replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6Z'));
    }
    
    // Parse COUNT (number of occurrences)
    const countMatch = rrule.match(/COUNT=(\d+)/);
    if (countMatch) {
      recurrenceInfo.count = parseInt(countMatch[1]);
    }
    
    return recurrenceInfo;
  }, []);

  // Format human-readable recurrence description
  const getRecurrenceDescription = useCallback((recurrenceInfo) => {
    if (!recurrenceInfo) return null;
    
    const { frequency, interval, daysOfWeek, endDate, count } = recurrenceInfo;
    
    let description = '';
    
    // Base frequency description
    if (frequency === 'weekly') {
      if (interval === 1) {
        description = 'Weekly';
      } else {
        description = `Every ${interval} weeks`;
      }
      
      // Add day of week for weekly events
      if (daysOfWeek.length > 0) {
        const dayNames = {
          'SU': 'Sunday',
          'MO': 'Monday',
          'TU': 'Tuesday',
          'WE': 'Wednesday',
          'TH': 'Thursday',
          'FR': 'Friday',
          'SA': 'Saturday'
        };
        const daysList = daysOfWeek.map(day => dayNames[day] || day).join(', ');
        description += ` on ${daysList}`;
      }
    } else if (frequency === 'daily') {
      if (interval === 1) {
        description = 'Daily';
      } else {
        description = `Every ${interval} days`;
      }
    } else if (frequency === 'monthly') {
      if (interval === 1) {
        description = 'Monthly';
      } else {
        description = `Every ${interval} months`;
      }
    } else if (frequency === 'yearly') {
      if (interval === 1) {
        description = 'Yearly';
      } else {
        description = `Every ${interval} years`;
      }
    } else {
      description = frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1) : 'Recurring';
    }
    
    // Add end condition
    if (endDate) {
      description += ` until ${endDate.toLocaleDateString('en-US', { timeZone: TIMEZONE })}`;
    } else if (count) {
      description += ` for ${count} occurrences`;
    }
    
    return description;
  }, []);

  // Format events from Google Calendar API response
  const formatEvents = useCallback((items, includeRecurrence = false) => {
    return items.map((item, index) => {
      // Parse start and end dates/times
      const startDateTime = item.start.dateTime || item.start.date;
      const endDateTime = item.end.dateTime || item.end.date;
      
      // Format the date and time strings
      const startDate = new Date(startDateTime);
      const endDate = new Date(endDateTime);
      
      // Get the date in Central Time for proper display and storage
      const centralDate = new Date(startDate.toLocaleString("en-US", {timeZone: TIMEZONE}));
      
      // Determine the event category (can be refined based on actual event data)
      let category = 'regular';
      if (item.summary && item.summary.toLowerCase().includes('workshop')) {
        category = 'workshop';
      } else if (item.summary && item.summary.toLowerCase().includes('tournament')) {
        category = 'tournament';
      } else if (item.summary && item.summary.toLowerCase().includes('online')) {
        category = 'online';
      }
      
      // Parse recurrence information if requested and available
      let recurrenceInfo = null;
      let recurrenceDescription = null;
      if (includeRecurrence && item.recurrence) {
        recurrenceInfo = parseRecurrence(item.recurrence);
        recurrenceDescription = getRecurrenceDescription(recurrenceInfo);
      }
      
      return {
        id: item.id || index,
        title: item.summary || 'Untitled Event',
        date: centralDate.toISOString().split('T')[0], // YYYY-MM-DD format in Central Time
        originalStartDate: startDate, // Keep the original date objects for timezone formatting
        originalEndDate: endDate,
        time: `${formatTime(startDate)} - ${formatTime(endDate)}`,
        location: item.location || 'TBD',
        description: item.description || 'No description available',
        category: category,
        isCancelled: (item.summary && item.summary.toLowerCase().includes('cancelled')) ||
                    (item.description && item.description.toLowerCase().includes('cancelled')),
        // Recurrence information
        recurrence: recurrenceInfo,
        recurrenceDescription: recurrenceDescription,
        isRecurring: !!item.recurrence,
        recurringEventId: item.recurringEventId || null
      };
    });
  }, [parseRecurrence, getRecurrenceDescription]);

  // Check for cancelled meetings
  useEffect(() => {
    const checkForCancellations = async () => {
      setCheckingCancellation(true);
      try {
        // Get current date and check upcoming Sundays for the next 4 weeks
        const today = new Date();
        const fourWeeksFromNow = new Date(today);
        fourWeeksFromNow.setDate(today.getDate() + 28);
        
        const timeMin = today.toISOString();
        const timeMax = fourWeeksFromNow.toISOString();
        
        // Fetch events from Google Calendar
        const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events?key=${API_KEY}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&timeZone=${TIMEZONE}`;
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch calendar events');
        }
        
        const data = await response.json();
        
        // Format and filter events
        const events = data.items.map(item => {
          const startDateTime = item.start.dateTime || item.start.date;
          const startDate = new Date(startDateTime);
          
          return {
            id: item.id,
            title: item.summary || '',
            description: item.description || '',
            startDate: startDate,
            isRegularMeeting: !item.summary || (!item.summary.toLowerCase().includes('workshop') && 
                                                !item.summary.toLowerCase().includes('tournament') && 
                                                !item.summary.toLowerCase().includes('online')),
            isCancelled: (item.summary && item.summary.toLowerCase().includes('cancelled')) ||
                        (item.description && item.description.toLowerCase().includes('cancelled'))
          };
        });
        
        // Filter to only regular meetings and sort by date
        const regularMeetings = events.filter(event => event.isRegularMeeting).sort((a, b) => a.startDate - b.startDate);
        
        if (regularMeetings.length > 0) {
          const nextMeeting = regularMeetings[0];
          
          if (nextMeeting.isCancelled) {
            // Find the next non-cancelled meeting
            const nextNonCancelledMeeting = regularMeetings.find(meeting => !meeting.isCancelled);
            
            setCancellationInfo({
              cancelledDate: nextMeeting.startDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric'
              }),
              nextMeetingDate: nextNonCancelledMeeting ? nextNonCancelledMeeting.startDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric'
              }) : 'TBD'
            });
          } else {
            setCancellationInfo(null);
          }
        } else {
          setCancellationInfo(null);
        }
      } catch (error) {
        console.error('Error checking for cancellations:', error);
        setCancellationInfo(null);
      } finally {
        setCheckingCancellation(false);
      }
    };
    
    checkForCancellations();
  }, [API_KEY, CALENDAR_ID]);

  // Fetch upcoming events (all events happening this week)
  useEffect(() => {
    const fetchUpcomingEvents = async () => {
      setLoading(true);
      try {
        // Get current date in ISO format
        const today = new Date();
        const timeMin = today.toISOString();
        
        // Get date 1 week from now for events happening this week
        const oneWeekFromNow = new Date(today);
        oneWeekFromNow.setDate(today.getDate() + 6);
        const timeMax = oneWeekFromNow.toISOString();
        
        // Fetch all individual events for this week (both recurring and non-recurring)
        const individualUrl = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events?key=${API_KEY}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&timeZone=${TIMEZONE}`;
        
        const response = await fetch(individualUrl);
        
        if (!response.ok) {
          throw new Error('Failed to fetch upcoming events from Google Calendar');
        }
        
        const data = await response.json();
        
        // Transform all individual events for this week
        const formattedEvents = formatEvents(data.items);
        
        console.log('All events happening this week:', formattedEvents);
        
        setUpcomingEvents(formattedEvents);
        setError(null);
      } catch (err) {
        console.error('Error fetching upcoming events:', err);
        setError('Failed to load upcoming events. Please try again later.');
        setUpcomingEvents([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUpcomingEvents();
  }, [formatEvents, API_KEY, CALENDAR_ID]);

  // Fetch special events (events more than a week away but within 3 months)
  useEffect(() => {
    const fetchSpecialEvents = async () => {
      setSpecialEventsLoading(true);
      try {
        // Get current date in ISO format
        const today = new Date();
        
        // Start from 1 week from now (exclude events already shown in "Happening This Week")
        const oneWeekFromNow = new Date(today);
        oneWeekFromNow.setDate(today.getDate() + 6);
        const timeMin = oneWeekFromNow.toISOString();
        
        // Get date 3 months from now for special events
        const threeMonthsFromNow = new Date(today);
        threeMonthsFromNow.setMonth(today.getMonth() + 3);
        const timeMax = threeMonthsFromNow.toISOString();
        
        // Fetch all individual events more than a week away but within 3 months
        const individualUrl = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events?key=${API_KEY}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&timeZone=${TIMEZONE}`;
        
        const response = await fetch(individualUrl);
        
        if (!response.ok) {
          throw new Error('Failed to fetch special events from Google Calendar');
        }
        
        const data = await response.json();
        
        // Transform all individual events in this time range
        const formattedEvents = formatEvents(data.items);
        
        // Filter to only show non-recurring events (true special events)
        const nonRecurringEvents = formattedEvents.filter(event => {
          const isRecurring = !!event.recurringEventId;
          return !isRecurring;
        });
        
        console.log('All events more than a week away:', formattedEvents);
        console.log('Non-recurring special events:', nonRecurringEvents);
        
        setSpecialEvents(nonRecurringEvents);
      } catch (err) {
        console.error('Error fetching special events:', err);
        // Don't show error for special events specifically, just log it
        setSpecialEvents([]);
      } finally {
        setSpecialEventsLoading(false);
      }
    };
    
    fetchSpecialEvents();
  }, [formatEvents, API_KEY, CALENDAR_ID]);

  // Fetch events for the displayed month
  useEffect(() => {
    const fetchMonthEvents = async () => {
      setCalendarLoading(true);
      try {
        // Get first day of the current displayed month
        const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
        
        // Set time to beginning and end of day
        firstDay.setHours(0, 0, 0, 0);
        lastDay.setHours(23, 59, 59, 999);
        
        const timeMin = firstDay.toISOString();
        const timeMax = lastDay.toISOString();
        
        // Construct API URL with necessary parameters
        const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events?key=${API_KEY}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&timeZone=${TIMEZONE}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Failed to fetch month events from Google Calendar');
        }
        
        const data = await response.json();
        
        // Transform the Google Calendar events into our app's format
        const formattedEvents = formatEvents(data.items);
        
        setEvents(formattedEvents);
        
      } catch (err) {
        console.error('Error fetching month events:', err);
        // Don't show error for calendar specifically, just log it
        setEvents([]);
      } finally {
        setCalendarLoading(false);
      }
    };
    
    fetchMonthEvents();
  }, [currentMonth, formatEvents, API_KEY, CALENDAR_ID]);

  // Generate calendar data
  useEffect(() => {
    const generateCalendar = () => {
      const month = currentMonth.getMonth();
      const year = currentMonth.getFullYear();
      
      // Get the first day of the month
      const firstDay = new Date(year, month, 1);
      const startingDay = firstDay.getDay();
      
      // Get the number of days in the month
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      // Create calendar array
      const calendarDays = [];
      
      // Previous month's days
      for (let i = 0; i < startingDay; i++) {
        calendarDays.push({
          day: new Date(year, month, -startingDay + i + 1).getDate(),
          month: month - 1,
          year,
          currentMonth: false
        });
      }
      
      // Current month's days
      for (let i = 1; i <= daysInMonth; i++) {
        // Check if there are events on this day
        const formattedDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
        const dayEvents = events.filter(event => event.date === formattedDate);
        const hasEvents = dayEvents.length > 0;
        
        calendarDays.push({
          day: i,
          month,
          year,
          currentMonth: true,
          hasEvents,
          events: dayEvents,
          eventCategories: dayEvents.map(event => event.category)
        });
      }
      
      // Fill remaining slots with next month's days
      const totalDaysToShow = 42; // 6 rows of 7 days
      if (calendarDays.length < totalDaysToShow) {
        const daysToAdd = totalDaysToShow - calendarDays.length;
        for (let i = 1; i <= daysToAdd; i++) {
          calendarDays.push({
            day: i,
            month: month + 1,
            year,
            currentMonth: false
          });
        }
      }
      
      setCalendar(calendarDays);
    };
    
    generateCalendar();
  }, [currentMonth, events]);

  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Select a date and filter events
  const selectDate = (day) => {
    if (!day.currentMonth) return;
    
    const selectedDate = new Date(day.year, day.month, day.day);
    setSelectedDate(selectedDate);
  };

  // Clear date selection
  const clearDateSelection = () => {
    setSelectedDate(null);
  };

  // Format month name
  const formatMonth = () => {
    return new Intl.DateTimeFormat('en-US', { 
      month: 'long', 
      year: 'numeric',
      timeZone: TIMEZONE 
    }).format(currentMonth);
  };

  // Get category badge
  const getCategoryBadge = (category) => {
    switch (category) {
      case 'regular':
        return <Badge bg="primary">Regular Meeting</Badge>;
      case 'workshop':
        return <Badge bg="success">Workshop</Badge>;
      case 'tournament':
        return <Badge bg="danger">Tournament</Badge>;
      case 'online':
        return <Badge bg="info">Online</Badge>;
      default:
        return <Badge bg="secondary">Other</Badge>;
    }
  };

  // Render event indicators for a day
  const renderEventIndicators = (day) => {
    if (!day.hasEvents) return null;
    
    const categories = [...new Set(day.eventCategories)];
    
    return (
      <div className="event-indicators">
        {categories.map((category, index) => (
          <span key={index} className={`event-indicator ${category}`}></span>
        ))}
      </div>
    );
  };

  return (
    <div className="events-page">
      {/* Hero Section */}
      <section className="events-hero mb-5">
        <h2>Events Calendar</h2>
        <p className="lead">
          Join us for our regular meetings and special events. Click on a date to see events scheduled for that day.
          Browse our upcoming regular meetings and discover special workshops, tournaments, and activities.
          <span className="ms-2 text-muted">(All times shown in CST)</span>
        </p>
      </section>

      {/* Cancellation Notice Widget */}
      {!checkingCancellation && cancellationInfo && (
        <Alert variant="warning" className="cancellation-notice mb-4">
          <Alert.Heading className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Meeting Cancelled
          </Alert.Heading>
          <p className="mb-2">
            <strong>The regular meeting on {cancellationInfo.cancelledDate} has been cancelled.</strong>
          </p>
          <p className="mb-0">
            <i className="bi bi-calendar-check me-1"></i>
            The next meeting will be on <strong>{cancellationInfo.nextMeetingDate}</strong> at 2:00 PM.
          </p>
        </Alert>
      )}

      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      <Row>
        <Col md={8}>
          {calendarLoading ? (
            <div className="text-center my-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading calendar...</span>
              </div>
              <p className="mt-3">Loading calendar...</p>
            </div>
          ) : (
            <div className="calendar-container">
              <div className="calendar-header">
                <Button variant="outline-secondary" onClick={prevMonth}>&lt;</Button>
                <h3>{formatMonth()}</h3>
                <Button variant="outline-secondary" onClick={nextMonth}>&gt;</Button>
              </div>
              

              
              <div className="calendar-grid">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                  <div key={index} className="calendar-cell header">
                    {day}
                  </div>
                ))}
                
                {calendar.map((day, index) => (
                  <div 
                    key={index} 
                    className={`calendar-cell ${!day.currentMonth ? 'faded' : ''} ${day.hasEvents ? 'has-events' : ''} ${
                      selectedDate && 
                      selectedDate.getDate() === day.day && 
                      selectedDate.getMonth() === day.month && 
                      selectedDate.getFullYear() === day.year 
                        ? 'selected' : ''
                    }`}
                    onClick={() => selectDate(day)}
                  >
                    <span className="day-number">{day.day}</span>
                    {renderEventIndicators(day)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Col>
        
        <Col md={4}>
          {/* Show selected date events here */}
          {selectedDate && (
            <div className="selected-date-events mb-4">
              <h5>
                Events on {formatDate(selectedDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h5>
              {events.filter(event => {
                const formattedDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                return event.date === formattedDate;
              }).length === 0 ? (
                <p className="text-muted">No events scheduled for this date.</p>
              ) : (
                events.filter(event => {
                  const formattedDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                  return event.date === formattedDate;
                }).map(event => (
                  <Card key={event.id} className="mb-2 event-card-compact">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{event.title}</h6>
                          <small className="text-muted d-block mb-2">{event.time}</small>
                          <p className="mb-1 small">{event.description}</p>
                          <small className="text-muted">
                            <strong>Location:</strong> {event.location}
                          </small>
                        </div>
                        <div className="ms-2">
                          {getCategoryBadge(event.category)}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={clearDateSelection}
                className="mt-2"
              >
                Clear Selection
              </Button>
            </div>
          )}

          <div className="filter-section">
            <div className="calendar-info">
              <h5>Add Our Calendar</h5>
              <p>
                Add our calendar to your personal calendar to stay updated with all events and meetings.
              </p>
              <p>
                <a 
                  href={`https://calendar.google.com/calendar/render?cid=${encodeURIComponent(CALENDAR_ID)}&ctz=${TIMEZONE}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                >
                  <i className="bi bi-calendar-plus"></i> Add to Google Calendar
                </a>
              </p>
              <p>
                <a 
                  href={`https://calendar.google.com/calendar/ical/${encodeURIComponent(CALENDAR_ID)}/public/basic.ics`} 
                  className="btn btn-sm btn-outline-secondary"
                >
                  <i className="bi bi-download"></i> Download iCal File
                </a>
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Move upcoming events list here, below the calendar */}
      <div className="upcoming-events-section mt-4">
        <h3>Happening This Week</h3>
        
        {loading ? (
          <div className="text-center my-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading events...</span>
            </div>
            <p className="mt-3">Loading events...</p>
          </div>
        ) : upcomingEvents.length === 0 ? (
          <p>No regular meetings scheduled for the next week.</p>
        ) : (
          <>
            {/* Show resume message if there are cancelled events */}
            {upcomingEvents.some(event => event.isCancelled) && (
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Note:</strong> Regular meetings will resume next week at the usual time (Sundays, 2:00 PM - 6:00 PM).
              </Alert>
            )}
            
            <Row>
              {upcomingEvents.map(event => (
                <Col md={6} lg={4} key={event.id} className="mb-3">
                  <Card className={`h-100 event-card ${event.isCancelled ? 'cancelled-event' : ''}`}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <Card.Title className={`h6 ${event.isCancelled ? 'text-muted' : ''}`}>
                          {event.isCancelled && <i className="bi bi-x-circle-fill text-danger me-1"></i>}
                          {event.title}
                          {event.isCancelled && <span className="badge bg-danger ms-2">CANCELLED</span>}
                        </Card.Title>
                        {!event.isCancelled && getCategoryBadge(event.category)}
                      </div>
                      <Card.Subtitle className="mb-2 text-muted small">
                        {formatDate(event.originalStartDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        {' • '}{event.time}
                      </Card.Subtitle>
                      <Card.Text className={`small ${event.isCancelled ? 'text-muted' : ''}`}>
                        {event.description}
                      </Card.Text>
                      <div className="event-details">
                        <div className="location small">
                          <strong>Location:</strong> {event.location}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
      </div>

      {/* Special Events Section */}
      <div className="special-events-section mt-5">
        <h3>Special Events</h3>
        <p className="text-muted">Non-recurring special events more than a week away, including workshops, tournaments, and other one-time activities in the next 3 months.</p>
        
        {specialEventsLoading ? (
          <div className="text-center my-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading special events...</span>
            </div>
            <p className="mt-3">Loading special events...</p>
          </div>
        ) : specialEvents.length === 0 ? (
          <div className="text-center my-5">
            <div className="no-events-placeholder">
              <h5 className="text-muted">No Special Events Scheduled</h5>
              <p className="text-muted">Check back later for workshops, tournaments, and other exciting activities!</p>
            </div>
          </div>
        ) : (
          <Row>
            {specialEvents.map(event => (
              <Col md={6} lg={4} key={event.id} className="mb-4">
                <Card className="h-100 event-card">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Card.Title className="h6">{event.title}</Card.Title>
                      {getCategoryBadge(event.category)}
                    </div>
                    <Card.Subtitle className="mb-2 text-muted small">
                      {formatDate(event.originalStartDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      {' • '}{event.time}
                    </Card.Subtitle>
                    <Card.Text className="small">{event.description}</Card.Text>
                    <div className="event-details">
                      <div className="location small">
                        <strong>Location:</strong> {event.location}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>
    </div>
  );
};

export default Events;